import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ImprovementItem, useImprovements } from '../components/Improvements.js';
import { PageTitle } from '../components/Layouts.js';
import { LoadError, Loading, Notice } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { useMe } from '../session.js';

const PERIODS = [7, 30, 90] as const;

/** "Was sich verbessert hat" (T-36, owner's remark 4): the good-news feed of a site. */
export function Changes() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const [days, setDays] = useState<(typeof PERIODS)[number]>(30);
  const feed = useImprovements(siteId, days);

  return (
    <>
      <PageTitle>{t('changes.title')}</PageTitle>
      <p className="-mt-4 mb-4 max-w-prose text-slate-700">{t('changes.intro')}</p>
      <div role="group" aria-label={t('changes.period')} className="mb-4 flex flex-wrap gap-1">
        {PERIODS.map((d) => (
          <button
            key={d}
            type="button"
            aria-pressed={days === d}
            onClick={() => setDays(d)}
            className={`min-h-11 rounded-md border px-3 font-semibold ${days === d ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-300 bg-white text-slate-900'}`}
          >
            {t('changes.days', { count: d })}
          </button>
        ))}
      </div>
      {feed.isPending ? <Loading /> : null}
      {feed.isError ? (
        <LoadError message={errorMessage(feed.error, t)} onRetry={() => void feed.refetch()} />
      ) : null}
      {feed.data ? (
        feed.data.items.length === 0 ? (
          <Notice kind="info">{t('changes.empty')}</Notice>
        ) : (
          <ul className="flex flex-col gap-3">
            {feed.data.items.map((item) => (
              <li key={`${item.kind}-${item.at}-${item.title}`}>
                <ImprovementItem item={item} timeZone={timeZone} />
              </li>
            ))}
          </ul>
        )
      ) : null}
    </>
  );
}
